import React from 'react'
import NextImage from 'next/image'

import {Box, Button, Heading, Text, Video, prefixUrl} from '@miro-site/core'

import type {LazyImageProps} from '@miro-site/core'

import styled, {useTheme} from 'styled-components'
import {TemplateActions} from '../../template-card/templateActions'

export interface PageHeaderProps extends React.RefAttributes<HTMLElement> {
  image: LazyImageProps
  templateActions: TemplateActions
  metadataId: string
  headline: string
  description: string
  paidTemplateDescription?: string
  wistiaVideoId?: string | null
  onUseTemplateClick: React.MouseEventHandler<HTMLElement>
}

const MediaContainer = styled(Box)`
  ${({theme}) =>
    theme.normalizeStyles({
      borderRadius: 8,
      overflow: 'hidden',
    })}
`

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  (
    {
      metadataId,
      image,
      headline,
      description,
      wistiaVideoId,
      onUseTemplateClick,
      templateActions,
      paidTemplateDescription,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme()

    return (
      <Box
        ref={ref}
        data-track-place="template-page-header"
        $pb={[20, 28, 28, 28]}
        $styles={{
          display: [undefined, 'flex', 'flex', 'flex'],
          flexWrap: [undefined, 'nowrap', 'nowrap', 'nowrap'],
          flexDirection: [undefined, 'row-reverse', 'row-reverse', 'row-reverse'],
        }}
        {...props}
      >
        <Box
          $mb={[40, null, null, null]}
          $styles={{
            position: 'relative',
            borderRadius: 8,
            width: [325, 720, 720, 720],
            height: wistiaVideoId ? undefined : [216, 480, 480, 480],
          }}
        >
          <MediaContainer>
            {wistiaVideoId ? (
              <Video id={wistiaVideoId} />
            ) : (
              <NextImage
                src={prefixUrl(image.original.webp)}
                unoptimized
                alt={image.name}
                priority
                width={720}
                height={480}
              />
            )}
          </MediaContainer>
        </Box>
        <Box
          $styles={{textAlign: ['center', 'left', 'left', 'left'], width: ['100%', 360, 360, 360]}}
          $mr={[null, 40, 40, 40]}
        >
          <Heading as="h1" $variant={['h3', 'h2', 'h2', 'h2']} $mb={[20, 24, 24, 24]}>
            {headline}
          </Heading>
          <Text $variant="p1" $transparency={0.3} $mb={40}>
            {description}
          </Text>
          <Button
            data-track-id="use-template"
            data-template-id={metadataId}
            $buttonType="primary"
            $size="medium"
            $withArrow
            onClick={onUseTemplateClick}
            $mb={[12, 15]}
          >
            {templateActions.USE_TEMPLATE}
          </Button>
          {paidTemplateDescription ? (
            <Text $variant="p2" $styles={{color: theme.templates.colors.secondary60}}>
              {paidTemplateDescription}
            </Text>
          ) : null}
        </Box>
      </Box>
    )
  },
)

PageHeader.displayName = 'PageHeader'

export default PageHeader
